import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePermissions } from '@/contexts/unified-permissions';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { validatePasswordStrength } from '@/utils/validation';
import { PasswordStrengthIndicator } from '@/components/ui/PasswordStrengthIndicator';

export function PasswordChangeForm() {
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const { session } = usePermissions();

  // New password validation handler
  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);
    setNewPasswordError('');

    if (value.trim() !== '') {
      const validation = validatePasswordStrength(value, 8);
      if (!validation.isValid) {
        setNewPasswordError(validation.errorMessage || 'Password requirements not met');
      }
    }
  };

  // Confirm password validation handler
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setConfirmPasswordError('');

    if (value.trim() !== '' && newPassword.trim() !== '' && value !== newPassword) {
      setConfirmPasswordError('Passwords do not match');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewPasswordError('');
    setConfirmPasswordError('');

    if (!session) {
      toast.error('No session found. Please sign in again.');
      return;
    }

    if (!password || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields.');
      return;
    }

    // Validate new password strength
    const passwordValidation = validatePasswordStrength(newPassword, 8);
    if (!passwordValidation.isValid) {
      setNewPasswordError(passwordValidation.errorMessage || 'Password requirements not met');
      return;
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Password changed successfully!');
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'An error occurred while changing the password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
          Current Password
        </label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter current password"
          required
        />
      </div>
      <div>
        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
          New Password
        </label>
        <Input
          id="newPassword"
          type="password"
          value={newPassword}
          onChange={handleNewPasswordChange}
          placeholder="Enter new password"
          required
          className={newPasswordError ? 'border-red-500 focus:border-red-500' : ''}
        />
        {newPasswordError && (
          <p className="mt-1 text-sm text-red-600">{newPasswordError}</p>
        )}
        <PasswordStrengthIndicator
          password={newPassword}
          minLength={8}
          showRequirements={true}
          className="mt-2"
        />
      </div>
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
          Confirm New Password
        </label>
        <Input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          placeholder="Confirm new password"
          required
          className={confirmPasswordError ? 'border-red-500 focus:border-red-500' : ''}
        />
        {confirmPasswordError && (
          <p className="mt-1 text-sm text-red-600">{confirmPasswordError}</p>
        )}
      </div>
      <Button type="submit" disabled={isLoading}>
        {isLoading ? 'Changing...' : 'Change Password'}
      </Button>
    </form>
  );
}
